import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import MembershipCardPreview from "@/components/MembershipCardPreview";
import { fmtDate } from "@/lib/api";
import { downloadCardImage, cardFileName } from "@/lib/downloadCard";

// Dialog to view a membership card and download it as a PNG image.
export default function MembershipCardModal({ open, onOpenChange, membership }) {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  if (!membership) return null;

  const onDownload = async () => {
    setDownloading(true);
    try {
      await downloadCardImage(
        cardRef.current,
        cardFileName(membership.membership_number, membership.patient_name)
      );
      toast.success("Card downloaded");
    } catch (e) {
      toast.error("Could not download card");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Membership card</DialogTitle>
          <DialogDescription>
            {membership.patient_name} · {membership.membership_number}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 flex justify-center">
          <MembershipCardPreview
            innerRef={cardRef}
            variant="flat"
            name={membership.patient_name}
            mobile={membership.patient_mobile}
            membershipNumber={membership.membership_number}
            issued={fmtDate(membership.issued_at)}
            expires={fmtDate(membership.expires_at)}
          />
        </div>

        <Button
          onClick={onDownload}
          disabled={downloading}
          className="w-full h-11 bg-gradient-primary text-primary-foreground rounded-xl shadow-elegant"
        >
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {downloading ? "Preparing image…" : "Download card as image"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
